package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.SharedGrantInfoDto
import com.advaita.cloud.drive.shared.SharedPageResponse
import com.advaita.cloud.drive.shared.SourceStatus
import com.advaita.cloud.drive.shared.Status
import com.advaita.cloud.drive.ui.api.FilesApi
import com.advaita.cloud.drive.ui.api.RedirectingException
import com.advaita.cloud.drive.ui.api.ShareApiException
import com.advaita.cloud.drive.ui.components.ShareCap
import com.advaita.cloud.drive.ui.components.capForError
import com.advaita.cloud.drive.ui.components.capForSharedList
import com.advaita.cloud.drive.ui.components.capSentence
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

/** The reads and the one write that Shared with me needs. Injected, so jsTest can script them. */
interface SharedBackend {
    suspend fun shared(): SharedPageResponse
    suspend fun folder(folderId: String): List<FileDto>
    suspend fun revoke(grantId: String)
}

object HttpSharedBackend : SharedBackend {
    override suspend fun shared() = FilesApi.listShared()
    override suspend fun folder(folderId: String) = FilesApi.listFolderById(folderId)
    override suspend fun revoke(grantId: String) = FilesApi.revokeShare(grantId)
}

/** The banner for `sharesStatus: "rejected"`: the details are missing, and signing in again would not help. */
const val SHARES_REJECTED_SENTENCE = "Sharing details are unavailable right now. Your own files still work."

/** How a [SharedWithMeStore.revealPath] walk ended. */
enum class RevealResult { OPENED, GONE, INTERRUPTED }

/** The per-row actions a Shared with me row shows beyond Download and Preview ([SharedWithMeStore.rowActions]). */
data class SharedRowActions(val share: Boolean, val delete: Boolean, val extract: Boolean)

/** The sharer, the level and Leave, for one shared root (S7). */
data class SharedRowInfo(val sharedBy: String, val level: String, val leavable: Boolean, val expiresAt: String? = null)

/**
 * ADV-1330 F3 (ruling S7): the state of Shared with me. DOM-free, like [SearchStore].
 *
 * The list is `GET /api/shared`. With the share UI on, each root also carries the sharer, the
 * level and the grant id. The view never shows an outage as an empty list:
 *
 * - The list itself `503`s: [bannerCap] is the outage cap, the rows of the last good read stay,
 *   and with none [showTable] is false, so no "nothing is shared" line can appear.
 * - The grants read is down (`sharesStatus: unavailable`): the same banner, over a list that is
 *   still complete. Example: the vault is down, and Lena sees "Sharing is temporarily
 *   unavailable. Your own files still work."
 * - A `404` means the checks are off: [listCap] is S8 row 1, in place of the list.
 *
 * A shared folder opens by id (ADV-1329 D8). [trail] is the folders opened below the roots.
 *
 * [background] starts the store's background re-reads (the processing poll and the wait for new
 * rows, ADV-1330). It is a seam with the production default, so a test runs them on its own
 * scheduler.
 */
class SharedWithMeStore(
    private val backend: SharedBackend = HttpSharedBackend,
    private val background: (suspend () -> Unit) -> Job = { block -> MainScope().launch { block() } },
) {
    var items: List<FileDto> by mutableStateOf(emptyList())
        private set
    var trail: List<FileDto> by mutableStateOf(emptyList())
        private set
    /** True until the first [load] settles, so a deep link says "Loading…", not "nothing shared". */
    var loading: Boolean by mutableStateOf(true)
        private set
    var listCap: ShareCap? by mutableStateOf(null)
        private set
    var bannerCap: ShareCap? by mutableStateOf(null)
        private set
    var listUnavailable: Boolean by mutableStateOf(false)
        private set
    var error: String? by mutableStateOf(null)
        private set
    var truncated: Boolean by mutableStateOf(false)
        private set

    /**
     * Tim (2026-09-28): the rows ticked in the open shared folder, for the selection bar. Its
     * own state, never mixed with My Drive's. It clears as soon as a load of another folder, or
     * of the roots, starts (review: so the bar never acts on a folder that loads or failed), and
     * a re-read of the same folder drops the ticks on rows that are gone. A plain Set: reassignment is the
     * recomposition signal.
     */
    var selectedIds: Set<String> by mutableStateOf(emptySet())
        private set

    /** ADV-1465: the last row a click without Shift ticked or unticked. Null after a clear or a sort change. */
    private var selectionAnchor: String? = null

    fun toggleSelected(id: String) {
        selectedIds = if (id in selectedIds) selectedIds - id else selectedIds + id
        selectionAnchor = id
    }

    /**
     * ADV-1465: a checkbox click. With [shift] and an anchor in [order] (the rows as the table
     * shows them), the rows from the anchor to [id] are ticked. Else the click toggles [id] and
     * [id] becomes the anchor.
     */
    fun clickSelect(id: String, shift: Boolean, order: List<String>) {
        val anchor = selectionAnchor
        if (shift && anchor != null && anchor in order) {
            selectedIds = rangeSelect(selectedIds, anchor, id, order)
        } else {
            toggleSelected(id)
        }
    }

    /** ADV-1465: a sort change in the table resets the Shift+click anchor. */
    fun resetSelectionAnchor() { selectionAnchor = null }

    /** The header checkbox: tick every row of the open folder, or clear them all if all are ticked. */
    fun toggleSelectAllVisible() {
        val visible = items.map { it.id }.toSet()
        if (visible.isEmpty()) return
        if (selectedIds.containsAll(visible)) {
            selectedIds = selectedIds - visible
            // ADV-1465 review: a header clear also clears the Shift+click anchor.
            selectionAnchor = null
        } else {
            selectedIds = selectedIds + visible
        }
    }

    fun clearSelection() {
        selectionAnchor = null
        if (selectedIds.isNotEmpty()) selectedIds = emptySet()
    }

    /** Keeps only the ticks on rows that [listed] still has. */
    private fun pruneSelection(listed: List<FileDto>) {
        if (selectedIds.isEmpty()) return
        val kept = selectedIds.intersect(listed.map { it.id }.toSet())
        if (kept.size != selectedIds.size) selectedIds = kept
    }

    private var roots: List<FileDto> by mutableStateOf(emptyList())
    private var shares: Map<String, SharedGrantInfoDto> by mutableStateOf(emptyMap())
    private var peopleStatus: String? by mutableStateOf(null)
    private var sharesStatus: String? by mutableStateOf(null)
    private var seq = 0
    /** Bumped by [load] only, so a [recheck] knows when a newer full read replaced its answer. */
    private var loadSeq = 0

    /**
     * ADV-1330: true while Shared with me is on screen. [FileBrowserStore] sets it from its view,
     * so the background re-reads stop once the person leaves for My Drive.
     */
    var shown: () -> Boolean = { true }

    /** True when the grants read was rejected by the vault (`sharesStatus: "rejected"`). */
    var sharesRejected: Boolean by mutableStateOf(false)
        private set

    /**
     * The banner over the list. `unavailable` and a list `503` are the outage cap (row 3),
     * `refused` is row 5, and `rejected` (the vault answered, but not with the grants) is its own
     * sentence, with no advice to sign in again, because signing in would not help.
     */
    val bannerSentence: String?
        get() = bannerCap?.let { capSentence(it) } ?: if (sharesRejected) SHARES_REJECTED_SENTENCE else null

    /** False when there is no list to show: a cap in its place, an error, or an outage with no rows. */
    val showTable: Boolean get() = listCap == null && error == null && !(listUnavailable && items.isEmpty())

    /** True when the page carries the sharer and the level, so the table shows those columns. */
    val shareColumns: Boolean get() = trail.isEmpty() && sharesStatus == SourceStatus.OK

    /**
     * ADV-1330: true when the last grants read gave the grants (`sharesStatus: ok`). Else the
     * caller's level on a shared folder is not known, and the toolbar says so rather than
     * calling the person a viewer.
     */
    val grantsKnown: Boolean get() = sharesStatus == SourceStatus.OK

    /** The last grants read's `sharesStatus`, for the toolbar's tips ([createActions]). */
    val grantsStatus: String? get() = sharesStatus

    /**
     * The one place a `/api/shared` answer lands: the roots, the grants, the statuses and the
     * banner they give. [load] and [recheck] both use it, so the two can never disagree.
     */
    private fun applyPage(page: SharedPageResponse) {
        roots = page.items
        shares = page.shares
        peopleStatus = page.peopleStatus
        sharesStatus = page.sharesStatus
        truncated = page.truncated
        listUnavailable = false
        bannerCap = when (page.sharesStatus) {
            SourceStatus.UNAVAILABLE -> ShareCap.OUTAGE
            SourceStatus.REFUSED -> ShareCap.DOMAIN_FALLBACK
            else -> null
        }
        sharesRejected = page.sharesStatus == SourceStatus.REJECTED
    }

    /** Reads the roots again and returns to them. */
    suspend fun load() {
        val mine = ++seq
        loadSeq++
        processingPoll?.cancel()
        clearSelection()
        loading = items.isEmpty()
        try {
            val page = backend.shared()
            if (mine != seq) return
            applyPage(page)
            trail = emptyList()
            items = page.items
            listCap = null
            error = null
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            if (mine != seq) return
            val cap = (t as? ShareApiException)?.let(::capForSharedList)
            when (cap) {
                ShareCap.CHECKS_OFF -> { listCap = cap; items = emptyList(); roots = emptyList(); trail = emptyList() }
                ShareCap.OUTAGE -> { bannerCap = cap; listUnavailable = true; error = null }
                null -> error = "Couldn't load Shared with me. ${shareErrorMessage(t)}"
                else -> { listCap = cap; items = emptyList() }
            }
        } finally {
            if (mine == seq) loading = false
        }
    }

    /**
     * Opens a shared folder, or a folder inside one. ADV-1330 (fresh-eyes M-3): a root opened while
     * the grants are not known reads them first, so the toolbar can tell the level.
     */
    suspend fun openFolder(folder: FileDto) {
        if (trail.isEmpty() && !grantsKnown && !rereadGrants()) return
        openAt(trail + folder)
    }

    /** ADV-1330 (the shared-folder create slice): the open folder, or null at the top level. */
    val currentFolder: FileDto? get() = trail.lastOrNull()

    /**
     * ADV-1330 (the shared-folder create slice, Question 2 (a)): the caller's capabilities on the
     * open folder, from its shared root's grants (`trail[0]`). ADV-1330 (the level premise): the
     * union of every live grant ([SharedGrantInfoDto.effectiveCapabilities]), else, from an older
     * server, the chosen grant's capabilities. A folder inherits its root's
     * grants, so they are a sound lower bound, and the server checks each write anyway. Empty at
     * the top level, and when the grants read gave no info for the root. Example: Kai shares Run
     * 42 with Sequencing Core as Editor, so Lena holds `upload` in Run 42 and in Run 42/raw.
     */
    val currentCapabilities: List<String>
        get() = trail.firstOrNull()?.let { capabilitiesOf(it.id) }.orEmpty()

    /** The caller's capabilities on the root [rootId]: the union, else the chosen grant's. */
    private fun capabilitiesOf(rootId: String): List<String> =
        shares[rootId]?.let { g -> g.effectiveCapabilities.ifEmpty { g.capabilities } }.orEmpty()

    /**
     * The row [f]'s capabilities: its shared root's (`trail[0]`), or, on the roots list, the
     * row's own. A row inside a root inherits the root's grants.
     */
    fun rowCapabilities(f: FileDto): List<String> = capabilitiesOf(trail.firstOrNull()?.id ?: f.id)

    /**
     * Tim's rig screens (2026-09-28): the per-row actions that Shared with me shows beside
     * Download and Preview, each where the backend would accept it for the caller.
     *
     * - Share: `share` (the share dialog's preflight checks it again).
     * - Delete: `delete`. `DELETE /api/files/{id}` decides `delete` with no owner fence. No
     *   standard level holds it, so only a custom grant shows Delete. ADV-1412: inside a shared
     *   folder, also a row the caller added ([FileDto.mine]) while the caller holds `upload` on
     *   the open folder: the vault's creator arm. On the roots list [FileDto.mine] does not count,
     *   because a root is another person's item shared with the caller.
     * - Extract: `download`, on the roots list too. `ArchiveGate` checks `download` on the
     *   archive and `upload` on the destination. ADV-1529: the extract dialog offers the
     *   archive's own folder when the server allows it, and else a folder the person picks, so a
     *   zip shared on its own, or a Viewer's zip, extracts into the person's own space.
     *
     * Example: Lena, an Editor on Run 42, may extract run42-a.zip inside Run 42, and delete it,
     * because she added it. Kai's samples.csv has no Delete for her. Priya, a Viewer, gets
     * Download, and Extract into a folder in My files.
     */
    fun rowActions(f: FileDto): SharedRowActions {
        val caps = rowCapabilities(f)
        return SharedRowActions(
            share = "share" in caps,
            delete = if (trail.isEmpty()) "delete" in caps else mayDeleteInFolder(f, caps),
            extract = "download" in caps,
        )
    }

    /**
     * ADV-1330 (scope 4): lists the open folder again after a create in it. [quiet] keeps the
     * rows on screen while it reads (no "Loading…" between the files of one upload). At the top
     * level it does nothing: the create went into a folder that is no longer open.
     */
    suspend fun reopen(quiet: Boolean = false) {
        if (trail.isNotEmpty()) openAt(trail, quiet)
    }

    /**
     * ADV-1330 (scope 5): after a refusal in the open folder, reads the grants again and then the
     * folder, so [currentCapabilities] follows a level that changed meanwhile. The trail stays.
     *
     * The grants always land ([applyPage]), unless a newer [load] ran meanwhile: a quiet reopen
     * of the folder must not throw away the new level. A navigation meanwhile only skips the
     * folder read, and a failed grants read keeps the last good ones (the server decides each
     * write anyway). Example: Kai lowers Sequencing Core to Viewer while Lena has Run 42 open.
     * Her next upload is refused, and her buttons go off.
     */
    suspend fun recheck() {
        val path = trail
        if (path.isEmpty()) return
        if (!rereadGrants()) return
        if (trail == path) openAt(path)
    }

    /**
     * Reads `/api/shared` and lands its grants ([applyPage]). False when a newer [load] ran
     * meanwhile, so its answer stands. A failed read keeps the last good grants and is true.
     */
    private suspend fun rereadGrants(): Boolean {
        val loads = loadSeq
        try {
            val page = backend.shared()
            if (loads != loadSeq) return false
            applyPage(page)
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            if (loads != loadSeq) return false
        }
        return true
    }

    // ---- ADV-1330 (review HIGH-1): the background re-reads of the open folder ----------------

    private var processingPoll: Job? = null

    /**
     * The folder view's processing poll, as My Drive has: while a row of the open folder is
     * PENDING or PROCESSING, or has a pull that runs, list the folder again every
     * [PROCESSING_POLL_MS], quietly. It stops once every row settles, the folder changes, or Shared
     * with me leaves the screen. Example: Lena pulls a FASTQ into Run 42, and its row goes from
     * PENDING to READY with no reload.
     */
    private fun scheduleProcessingPoll() {
        processingPoll?.cancel()
        val folderId = currentFolder?.id ?: return
        if (items.none(::isProcessing)) return
        processingPoll = background {
            delay(PROCESSING_POLL_MS)
            if (shown() && currentFolder?.id == folderId) reopen(quiet = true)
        }
    }

    private fun isProcessing(f: FileDto): Boolean =
        f.status == Status.PENDING || f.status == Status.PROCESSING ||
            f.pullProgress?.status == "PENDING" || f.pullProgress?.status == "RUNNING"

    private val awaited = mutableListOf<(List<FileDto>) -> Boolean>()
    private var awaitedFolderId: String? = null
    private var awaitReadsLeft = 0
    private var awaitJob: Job? = null

    /**
     * ADV-1330 (review HIGH-1): wait for the rows a create just made in the folder [folderId]. A
     * sharee lists only rows the vault has registered, and the registration runs a moment after
     * the create, so the re-read straight after it can miss the new row. Each of [expect] is
     * true once its row is in the listing. While one is not, list the folder again every
     * [SHARED_POLL_MS], up to [ROW_WAIT_READS] times (about 30 s, counted again from the latest
     * create). The wait ends when the folder changes or Shared with me leaves the screen. Rows
     * still missing at the end show nothing: the next open lists them. Example: Lena uploads
     * run42-a.zip into Run 42, and it shows about 2 s later.
     */
    fun awaitRows(folderId: String, expect: List<(List<FileDto>) -> Boolean>) {
        if (awaitedFolderId != folderId) {
            awaited.clear()
            awaitedFolderId = folderId
        }
        awaited += expect
        if (currentFolder?.id == folderId) awaited.removeAll { it(items) }
        if (awaited.isEmpty()) return
        awaitReadsLeft = ROW_WAIT_READS
        if (awaitJob?.isActive == true) return
        awaitJob = background {
            while (awaitReadsLeft > 0 && awaited.isNotEmpty()) {
                delay(SHARED_POLL_MS)
                awaitReadsLeft--
                if (!shown() || currentFolder?.id != awaitedFolderId) break
                reopen(quiet = true)
                awaited.removeAll { it(items) }
            }
            awaited.clear()
        }
    }

    /**
     * Lists the last folder of [path] and, on success only, makes [path] the trail. A later
     * navigation (a newer [openAt], [load], or [goToDepth] 0) bumps the sequence, so a late
     * answer for an older one changes nothing. [quiet] goes to [quietRead].
     */
    private suspend fun openAt(path: List<FileDto>, quiet: Boolean = false) {
        if (quiet) return quietRead(path)
        val folder = path.last()
        val mine = ++seq
        if (path != trail) clearSelection()
        loading = true
        var listedOk = false
        try {
            val listed = backend.folder(folder.id)
            if (mine != seq) return
            if (path == trail) pruneSelection(listed)
            trail = path
            items = listed
            error = null
            listedOk = true
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            if (mine != seq) return
            error = "Couldn't open \"${folder.displayName}\". ${folderFailure(t)}"
        } finally {
            if (mine == seq) loading = false
        }
        if (listedOk) scheduleProcessingPoll()
    }

    /** One more try for the processing poll after a failed quiet read, then it waits for a success. */
    private var pollRetryLeft = 1

    /**
     * ADV-1330 (fresh-eyes HIGH-1): a background re-read of [path] (the processing poll,
     * [awaitRows], the reload after a create). It never cancels a navigation: it does not bump the
     * sequence, it does not run while one loads, and its answer is dropped when a navigation or a
     * [load] began meanwhile, or the trail moved. It keeps [loading] as it is, and a failure keeps
     * the rows on screen: the processing poll then tries once more. Example: Lena clicks raw while
     * Run 42's poll is due, and she lands in raw.
     */
    private suspend fun quietRead(path: List<FileDto>) {
        if (loading) return
        val mine = seq
        val listed = try {
            backend.folder(path.last().id)
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            if (mine == seq && !loading && trail == path && pollRetryLeft > 0) {
                pollRetryLeft--
                scheduleProcessingPoll()
            }
            return
        }
        if (mine != seq || loading || trail != path) return
        items = listed
        pruneSelection(listed)
        error = null
        pollRetryLeft = 1
        scheduleProcessingPoll()
    }

    /**
     * ADV-1330 (Tim, 2026-09-28): opens the folder at the owner's path [target], below the open
     * folder, for a search result. It walks down by id, one listing per level, from the open
     * folder (a search in a shared folder only finds rows under it).
     *
     * - [RevealResult.OPENED]: the folder is open (or already was).
     * - [RevealResult.GONE]: a path outside the open folder, a level no longer there, or a
     *   failed listing. Nothing changes.
     * - [RevealResult.INTERRUPTED]: the person moved during the walk (a breadcrumb click, Back,
     *   a tab click), or a folder is loading. The move wins, and nothing changes.
     */
    suspend fun revealPath(target: String): RevealResult {
        val start = trail
        var path = start
        if (path.isEmpty()) return RevealResult.GONE
        while (path.last().logicalPath != target) {
            val here = path.last().logicalPath
            if (!target.startsWith("$here/")) return RevealResult.GONE
            val children = try {
                backend.folder(path.last().id)
            } catch (e: CancellationException) {
                throw e
            } catch (e: RedirectingException) {
                throw e
            } catch (t: Throwable) {
                return if (trail != start) RevealResult.INTERRUPTED else RevealResult.GONE
            }
            if (trail != start || loading) return RevealResult.INTERRUPTED
            path = path + (children.firstOrNull { c ->
                c.kind == FileKind.FOLDER && (target == c.logicalPath || target.startsWith("${c.logicalPath}/"))
            } ?: return RevealResult.GONE)
        }
        if (trail != start || loading) return RevealResult.INTERRUPTED
        if (path != trail) openAt(path)
        return RevealResult.OPENED
    }

    /** Walks back to [depth] folders below the roots. 0 is the roots. */
    suspend fun goToDepth(depth: Int) {
        if (depth <= 0) {
            seq++
            processingPoll?.cancel()
            loading = false
            trail = emptyList()
            items = roots
            clearSelection()
            error = null
            return
        }
        if (depth >= trail.size) return
        openAt(trail.take(depth))
    }

    /** Drops a row the caller no longer reaches (after Leave) from every list this store keeps. */
    fun forget(fileId: String) {
        roots = roots.filter { it.id != fileId }
        items = items.filter { it.id != fileId }
        shares = shares - fileId
        selectedIds = selectedIds - fileId
    }

    /** The sharer, the level and Leave of a root row. Null inside a folder, or with no grant info. */
    fun rowInfo(f: FileDto): SharedRowInfo? {
        if (trail.isNotEmpty()) return null
        val s = shares[f.id] ?: return null
        // Names unavailable: a dash, like every other empty cell of the table.
        val by = if (s.sharedBy.name.isNullOrBlank() && peopleStatus != SourceStatus.OK) "—"
        else principalLabel("USER", s.sharedBy, peopleStatus)
        return SharedRowInfo(by, levelLabel(s.level), s.leavable, s.expiresAt)
    }

    /**
     * True when the caller's grant on [f]'s shared root holds `share`, so the row may offer
     * Share. Rows inside a shared folder take the root's grant. Without grant info, no Share:
     * the dialog would only answer with a refusal.
     */
    fun canShare(f: FileDto): Boolean = rowActions(f).share

    /** Removes the caller's own grant on a root. Null on success, else the one sentence to show. */
    suspend fun leave(f: FileDto): String? {
        val s = shares[f.id]
        if (s == null || !s.leavable) return "You can leave only a share made to you by name."
        return try {
            backend.revoke(s.grantId)
            // Gone at once: a 503 on the re-read keeps the last good rows, and must not keep this one.
            forget(f.id)
            load()
            null
        } catch (e: CancellationException) {
            throw e
        } catch (e: RedirectingException) {
            throw e
        } catch (t: Throwable) {
            (t as? ShareApiException)?.let(::capForError)?.let { capSentence(it) }
                ?: shareErrorMessage(t, grantRoute = true)
        }
    }

    private fun folderFailure(t: Throwable): String = when {
        t is ShareApiException && t.status == 404 -> "It is no longer shared with you."
        t is ShareApiException -> capForError(t)?.let { capSentence(it) } ?: shareErrorMessage(t)
        else -> shareErrorMessage(t)
    }
}

/** ADV-1330: how often [SharedWithMeStore.awaitRows] re-reads the open folder. */
internal const val SHARED_POLL_MS = 2_000L

/** ADV-1330: the processing poll's interval, the same as My Drive's. */
internal const val PROCESSING_POLL_MS = 3_000L

/** ADV-1330: how many re-reads [SharedWithMeStore.awaitRows] makes, about 30 s at [SHARED_POLL_MS]. */
internal const val ROW_WAIT_READS = 15
